// @ts-strict-ignore
import { CopiedRangeOnLineArticleInformation } from '../model/copied-range-on-line-article-information';
import { PasteArticlePresenter } from '../presenter/paste-article.presenter';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { SelectionArticleGroupIdentity } from '../model/vo/selection-article-group-identity';
import { ProjectHelper } from '../../../helper/project.helper';
import { RotateArticleUtils } from '@article/rotation/rotate-article-utils';
import { ArticleRotationGroup } from '@article/rotation/article-rotation-group';
import { RotateArticlePresenter } from '../presenter/rotate-article.presenter';
import { ProjectInfoPropertyValueDef } from '../../project/constants/project-info-property-value-def';
import { UserModel } from '@user/model/user.model';
import { Injectable } from '@angular/core';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ToastrService } from 'ngx-toastr';
import { ArticleManager } from '@article/manager/article.manager';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ReplaceLabelingArticleHelper } from '../../label/replace-labeling-helper/replace-labeling-article.helper';
import { ReplaceLabelingUtils } from '../../label/replace-labeling-helper/replace-labeling-utils';
import { SelectionModel } from '../model/selection.model';
import { PasteArticleProcessor } from '../processor/paste-article.processor';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { CarrierBlockManager } from './carrier-block.manager';
import { ClipboardManager } from './clipboard.manager';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { SelectionModelService } from '../service/selection-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { PasteArticleManager } from './paste-article.manager';
import { CopyPasteAdditionalOperations } from './copy-paste-additional-operations';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { BuildingBlockInfo } from './building-block-info';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { ReplaceBusbarService } from '@article/helper/replace/busbar/replace-busbar.service';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { AttachArticleError } from '../processor/add-article-error';

@Injectable({ providedIn: 'root' })
export class BuildingBlockPasteArticleManager extends PasteArticleManager {
  private _rangeOnLineArticleInformation: CopiedRangeOnLineArticleInformation = null;
  private _pastePresenter: PasteArticlePresenter;

  public manualInsertionMode: boolean = true;

  constructor(
    userModel: UserModel,
    projectModel: ProjectModel,
    projectArticleService: ProjectArticleService,
    selectionModel: SelectionModel,
    private selectionModelService: SelectionModelService,
    articleManager: ArticleManager,
    clipboardManager: ClipboardManager,
    pasteArticleProcessor: PasteArticleProcessor,
    undoRedoHelper: UndoRedoHelper,
    carrierBlockManager: CarrierBlockManager,
    localeResourceManager: LocaleResourceManager,
    toastrService: ToastrService,
    replaceLabelingHelper: ReplaceLabelingArticleHelper,
    undoRedoUtils: UndoRedoUtils,
    containerService: ContainerService,
    articleContainerUtils: ArticleContainerUtils,
    replaceLabelingUtils: ReplaceLabelingUtils,
    insertArticleHelperFactory: InsertArticleHelperFactory,
    insertArticleUtils: InsertArticleUtils,
    articleAccessoryManager: ArticleAccessoryManager,
    private rotateArticleUtils: RotateArticleUtils,
    private projectHelper: ProjectHelper,
    private rotateArticlePresenter: RotateArticlePresenter,
    copyPasteAdditionalOperations: CopyPasteAdditionalOperations,
    applicationRefreshService: ApplicationRefreshService,
    buildingBlockInfo: BuildingBlockInfo,
    private articleConnectionService: ArticleConnectionService,
    articleContainerService: ArticleContainerService,
    replaceBusbarHelper: ReplaceBusbarArticleHelper,
    replaceBusbarService: ReplaceBusbarService
  ) {
    super(
      userModel,
      projectModel,
      projectArticleService,
      selectionModel,
      articleManager,
      clipboardManager,
      pasteArticleProcessor,
      undoRedoHelper,
      carrierBlockManager,
      localeResourceManager,
      toastrService,
      replaceLabelingHelper,
      undoRedoUtils,
      containerService,
      articleContainerUtils,
      replaceLabelingUtils,
      insertArticleHelperFactory,
      insertArticleUtils,
      articleAccessoryManager,
      copyPasteAdditionalOperations,
      applicationRefreshService,
      buildingBlockInfo,
      articleContainerService,
      replaceBusbarHelper,
      replaceBusbarService
    );
  }

  public setRangeOnLineArticleInformation(value: CopiedRangeOnLineArticleInformation) {
    this._rangeOnLineArticleInformation = value;
  }

  public setPastePresenter(value: PasteArticlePresenter): void {
    this._pastePresenter = value;
  }

  public getRangeOnLineInfo() {
    return this._rangeOnLineArticleInformation;
  }

  protected override async finalizePasteOperations(): Promise<void> {
    this.applyCableFedSide();
    this.selectLastArticle();
    if (this._pastePresenter) {
      this.buildingBlockInfo.dispatchBuildingBlockAttached = true;
      await this._pastePresenter.pasteOperationCompletedHandler(this._allPasteOperationsSuccessfullyFinished);
    }
    this._rangeOnLineArticleInformation = null;
    this._pastePresenter = null;
  }

  private applyCableFedSide(): void {
    const cableFedSide: string = this.projectModel.currentInfoProperties.getString("buildingBlockCableFedSide");
    if (cableFedSide === ProjectInfoPropertyValueDef.BUILDING_BLOCK_CABLE_FED_SIDE_BOTTOM) {
      const createdContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(this.buildingBlockInfo.createdArticleUsageIds);
      let articleRotationGroups: ArticleRotationGroup[] = this.rotateArticleUtils.createArticleRotationGroups(createdContainers);
      articleRotationGroups = this.rotateArticleUtils.combineLinkedRotationGroups(articleRotationGroups);
      for (const rotationGroup of articleRotationGroups) {
        this.rotateArticlePresenter.rotateArticleRotationGroup(rotationGroup, 180);
      }
    }
  }

  protected displayToasterMessage(title: string, body: string = ''): void {
    // Do not display toaster message when adding building blocks via. eShop or Assistant
  }

  private selectLastArticle(): void {
    if (this.manualInsertionMode) {
      const createdArticles: ArticleContainer[] = this.containerService
        .findArticleContainersInGlobalByGuids(this.buildingBlockInfo.createdArticleUsageIds)
        .filter((c) => this.articleConnectionService.isArticleConnectedToMountingRail(c));
      if (ArrayUtils.isEmpty(createdArticles)) {
        return;
      }
      const insertArticleHelper: InsertArticleHelper = this.insertArticleHelperFactory.createInstanceByConnectedArticleContainer(createdArticles[0]);
      if (!insertArticleHelper) {
        return;
      }
      const lastArticleContainer: ArticleContainer = insertArticleHelper.sortArticles(createdArticles).pop();
      const articleSelectionIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(lastArticleContainer.articleUsage);
      this.selectionModelService.selectObject(articleSelectionIdentity, false, false);
    }
  }

  protected noRequiredConnectionFound(acceptingArticle: { article: ArticleVO, usage?: ArticleUsageVO }, requiringArticle: { article: ArticleVO, usage?: ArticleUsageVO }) {
    if (this.manualInsertionMode) {
      this.clearPasteOperationParameters();
      throw new AttachArticleError('noRequiredConnectionFound', acceptingArticle, requiringArticle);
    }
  }
}
